import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/lib/auth";
import loginImg from "@/assets/loginimage.jpg";

export default function SalesLogin() {
  const nav = useNavigate();
  const { login, user, loading } = useAuth();

  React.useEffect(() => {
    if (!loading && user && user.role === "salesperson") {
      nav("/sales", { replace: true });
    }
  }, [user, loading, nav]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email, password);
      if (user.role !== "salesperson") {
        toast.error("This page is for Sales Executives only.");
        return;
      }
      toast.success(`Welcome, ${user.business_name || user.email}`);
      nav("/sales", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login failed";
      if (err?.response?.status === 403 && detail.toLowerCase().includes("verify")) {
        toast.error("Email not verified. Contact your manager.");
      } else {
        toast.error(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAFA]">
      <div className="hidden md:block relative">
        <img src={loginImg} alt="Interior" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1A1C1E]/50 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <span className="overline text-white/80">Sales Executive Portal</span>
          <h2 className="mt-4 text-4xl font-display font-medium leading-tight">Visualize. Sell. Delight.</h2>
        </div>
      </div>
      <div className="flex items-center justify-center px-8 py-16">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between">
            <Logo size="md" />
            <button
              onClick={() => nav(-1)}
              className="flex items-center gap-1 text-sm text-[#5C5F62] hover:text-[#1A1C1E] transition"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          </div>
          <div className="mt-12 flex items-center gap-3">
            <span className="h-10 w-10 rounded-full bg-[#F3F1EC] flex items-center justify-center shrink-0">
              <Sparkles className="h-5 w-5 text-[#C86B53]" />
            </span>
            <div>
              <h1 className="text-3xl font-display font-semibold tracking-tight">Sales Executive Login</h1>
              <p className="mt-1 text-sm text-[#5C5F62]">Sign in to open your visualizer.</p>
            </div>
          </div>
          <form className="mt-10 space-y-5" onSubmit={onSubmit}>
            <div>
              <label className="text-sm font-medium text-[#1A1C1E]">Email</label>
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-white border border-[#E5E5E5] rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] transition"
                placeholder="you@store.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#1A1C1E]">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-white border border-[#E5E5E5] rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C86B53]/20 focus:border-[#C86B53] transition"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="neivai-btn-primary w-full disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
